import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import OfficeBackdrop from './components/office/OfficeBackdrop';
import { LanguageProvider } from './context/LanguageContext';
import { ConfirmDialogProvider } from './components/ConfirmDialog';
import { OfficeFeedbackProvider } from './components/office/OfficeFeedbackProvider';
import ProtectedRoute from './components/ProtectedRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Layout from './components/Layout';
import { OfficeCommunicationsProvider } from './context/OfficeCommunicationsContext';
import { MeetingSessionProvider } from './context/MeetingSessionContext';
import { API_BASE_URL as API_URL } from './apiBase';

import './index.css';
import './pages/FinDestaque2910.css'; // FIN_DESTAQUE_2910 (menu + pagina)
import './styles/console-components.css';
import './styles/console-metrics.css';

/** Alinhado com backend AURAX_SAAS_ENABLED (build: REACT_APP_SAAS_ENABLED). */
function isSaasEnabled() {
  const v = (process.env.REACT_APP_SAAS_ENABLED || '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] w-full items-center justify-center bg-background">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
        aria-label="A carregar"
      />
    </div>
  );
}

function lazyPage(factory) {
  const Comp = lazy(factory);
  return function LazyPage(props) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <Comp {...props} />
        </Suspense>
      </RouteErrorBoundary>
    );
  };
}

/** Host da URL HTTP do Web Player → só /player (nunca o CRM/Office). */
function useWebPlayerHostOnly() {
  const [state, setState] = useState(() => {
    try {
      const h = String(window.location.hostname || '').toLowerCase();
      if (h.startsWith('webplayer.')) return { ready: true, only: true };
    } catch (_) {
      /* ignore */
    }
    return { ready: false, only: false };
  });

  useEffect(() => {
    if (state.ready && state.only) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/branding`, {
          withCredentials: true,
          timeout: 8000,
        });
        if (cancelled) return;
        if (data?.webplayer_host_only) {
          setState({ ready: true, only: true });
          return;
        }
        const wp = String(data?.webplayer_http_url || '').trim();
        if (wp) {
          try {
            const u = new URL(wp.startsWith('http') ? wp : `https://${wp}`);
            const host = String(window.location.hostname || '').toLowerCase();
            if (u.hostname && u.hostname.toLowerCase() === host) {
              setState({ ready: true, only: true });
              return;
            }
          } catch (_) {
            /* ignore */
          }
        }
      } catch (_) {
        /* ignore */
      }
      if (!cancelled) setState({ ready: true, only: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [state.ready, state.only]);

  return state;
}

function ProtectedShell() {
  return (
    <ProtectedRoute>
      <MeetingSessionProvider>
        <OfficeCommunicationsProvider><Layout>
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </Layout></OfficeCommunicationsProvider>
      </MeetingSessionProvider>
    </ProtectedRoute>
  );
}

function PublicMeetingRoom({ mode }) {
  return (
    <MeetingSessionProvider>
      <MeetingRoom mode={mode} />
    </MeetingSessionProvider>
  );
}

/* Login/senha no bundle principal — evita ecrã «Atualização pendente» pós-deploy por chunk lazy em falta. */
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';

/* Restantes rotas públicas — carregam sob demanda */
import { Hide2810 } from './lib/semApps2810'; // SEM_ATIVAR_APPS_2810 / SEM_CARTEIRA_2810
import { useAuraxFlags } from './lib/auraxFlags'; // FIN_REVENDA_2910
const Landing = lazyPage(() => import('./pages/Landing'));
const Pricing = lazyPage(() => import('./pages/Pricing'));
const SaasSignup = lazyPage(() => import('./pages/SaasSignup'));
const RegisterReferral = lazyPage(() => import('./pages/RegisterReferral'));
const CheckoutPortal = lazyPage(() => import('./pages/CheckoutPortal'));
const ClientLicenseStore = lazyPage(() => import('./pages/ClientLicenseStore'));
const PlayerRenewal = lazyPage(() => import('./pages/PlayerRenewal'));
const WebPlayer = lazyPage(() => import('./pages/WebPlayer'));

/* App autenticado — cada página no seu chunk */
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const Communications = lazyPage(() => import('./pages/Communications'));
const AttendantMetrics = lazyPage(() => import('./pages/AttendantMetrics'));
const Chats = lazyPage(() => import('./pages/Chats'));
// AURAX_REDESIGN_2710 (GROK2)
const AtendimentoEspelho = lazyPage(() => import('./pages/Atendimento'));
const WhatsAppUnica0311 = lazyPage(() => import('./pages/WhatsAppUnica0311')); // ABA_WHATS_UNICA_0311 (switch private/ABA_WHATS_UNICA_OFF)
const FinanceiroDashboard0 = lazyPage(() => import('./pages/FinanceiroDashboard'));
const FinRevenda2910 = lazyPage(() => import('./pages/FinRevenda2910')); // FIN_REVENDA_2910
const FinDestaque2910 = lazyPage(() => import('./pages/FinDestaque2910')); // FIN_DESTAQUE_2910
// MENU_SO_WHATSAPP_0311: rotas antigas -> /whatsapp (switch private/MENU_SO_WHATSAPP_OFF)
function SoWhats0311({ children }) { const f = useAuraxFlags(); if (f?.menu_so_whatsapp) return <Navigate to={`/connections?aba=${window.location.pathname.startsWith('/chats') ? 'conversas' : window.location.pathname.startsWith('/atendimento') ? 'datacrazy' : 'conexao'}`} replace />; return children; }
function FinanceiroDashboard() { const f = useAuraxFlags(); if (f?.fin_destaque_2910) return <FinDestaque2910 />; return f?.fin_revenda_2910 ? <FinRevenda2910 /> : <FinanceiroDashboard0 />; }
const Meetings = lazyPage(() => import('./pages/Meetings'));
const Extras2910 = lazyPage(() => import('./pages/Extras2910')); // EXTRAS_2910
const MeetingRoom = lazyPage(() => import('./pages/MeetingRoom'));
const PublicSchedule = lazyPage(() => import('./pages/PublicSchedule'));
const PublicMeetingInvite = lazyPage(() => import('./pages/PublicMeetingInvite'));
const Contacts = lazyPage(() => import('./pages/Contacts'));
const Filter = lazyPage(() => import('./pages/Funnel'));
const Triggers = lazyPage(() => import('./pages/Triggers'));
const Recuperacao = lazyPage(() => import('./pages/Recuperacao'));
const Sales = lazyPage(() => import('./pages/Sales'));
const LogsFinanceiros = lazyPage(() => import('./pages/LogsFinanceiros'));
const LogsGlobais = lazyPage(() => import('./pages/LogsGlobais'));
const Ranking = lazyPage(() => import('./pages/Ranking'));
const Connections = lazyPage(() => import('./pages/Connections'));
const WhatsAppTudo0311 = lazyPage(() => import('./pages/WhatsAppTudo0311')); // MENU_SO_WHATSAPP_0311
const Team = lazyPage(() => import('./pages/Team'));
const Settings = lazyPage(() => import('./pages/Settings'));
const CommunitySupport = lazyPage(() => import('./pages/CommunitySupport'));
const Integrations = lazyPage(() => import('./pages/Integrations'));
const ApiIntegrations = lazyPage(() => import('./pages/ApiIntegrations'));
const Chatbot = lazyPage(() => import('./pages/Chatbot'));
import { AbasAntigasGuard } from './lib/abasAntigas0311'; // ABAS_ANTIGAS_WHATS_0311
const AuraXAI = lazyPage(() => import('./pages/AuraXAI'));
const Bonification = lazyPage(() => import('./pages/Bonification'));
const PublicBonus = lazyPage(() => import('./pages/PublicBonus'));
const XuiServers = lazyPage(() => import('./pages/GestorXui/XuiServers'));
const Permissions = lazyPage(() => import('./pages/Permissions'));
const XuiClients = lazyPage(() => import('./pages/GestorXui/Clients'));
const XuiResellers = lazyPage(() => import('./pages/GestorXui/Resellers'));
const MyPortfolio = lazyPage(() => import('./pages/GestorXui/MyPortfolio'));
const ResellerTreeMigration = lazyPage(() => import('./pages/GestorXui/M3uMigration'));
const InactiveResellers = lazyPage(() => import('./pages/GestorXui/InactiveResellers'));
const InactiveClients = lazyPage(() => import('./pages/GestorXui/InactiveClients'));
const XuiPlanPricing = lazyPage(() => import('./pages/GestorXui/PlanPricing'));
const XuiCreditPacksConfig = lazyPage(() => import('./pages/GestorXui/CreditPacksConfig'));
const ResellerBillingConfig = lazyPage(() => import('./pages/GestorXui/ResellerBillingConfig'));
const MyResellerBillingConfig = lazyPage(() => import('./pages/GestorXui/MyResellerBillingConfig'));
const XuiGatewayConfig = lazyPage(() => import('./pages/GestorXui/GatewayConfig'));
const XuiCreditLogs = lazyPage(() => import('./pages/GestorXui/CreditLogs'));
const XuiCredentialGeneration = lazyPage(() => import('./pages/GestorXui/CredentialGeneration'));
const XuiCategories = lazyPage(() => import('./pages/GestorXui/Categories'));
const IptvCatalog = lazyPage(() => import('./pages/GestorXui/IptvCatalog'));
const AppActivations = lazy(() => import('./pages/AppActivations'));
const PartnerApps = lazyPage(() => import('./pages/GestorXui/PartnerApps'));
const OwnPlayerPage = lazyPage(() => import('./pages/GestorXui/OwnPlayerPage'));
const XuiTests = lazyPage(() => import('./pages/GestorXui/Tests'));
const Vencimentos2910 = lazyPage(() => import('./pages/GestorXui/Vencimentos2910')); // VENCIMENTOS_2910
const PassoAPasso2910 = lazyPage(() => import('./pages/GestorXui/PassoAPasso2910')); // PASSO_A_PASSO_2910
const BuyCredits = lazyPage(() => import('./pages/BuyCredits'));
const NotificationTemplates = lazyPage(() => import('./pages/NotificationTemplates'));
const MasterTenants = lazyPage(() => import('./pages/MasterTenants'));
const MasterSetup = lazyPage(() => import('./pages/MasterSetup'));
const ResellerManual = lazyPage(() => import('./pages/ResellerManual'));
const AdminResellerManual = lazyPage(() => import('./pages/AdminResellerManual'));
const LiveConnections = lazyPage(() => import('./pages/LiveConnections'));
const ChavesApiRevenda0311 = lazyPage(() => import('./pages/ChavesApiRevenda0311')); // API_REVENDA_0311
const ConfigurarRobo0311 = lazyPage(() => import('./pages/ConfigurarRobo0311')); // CONFIG_ROBO_0311

function AppRoutes() {
  const saas = isSaasEnabled();
  const { ready, only: playerHostOnly } = useWebPlayerHostOnly();

  if (!ready) return <PageFallback />;

  if (playerHostOnly) {
    return (
      <Routes>
        <Route path="/player" element={<WebPlayer />} />
        <Route path="*" element={<Navigate to="/player" replace />} />
      </Routes>
    );
  }

  return (
        <Routes>
          {/* Public Routes */}
          {saas ? (
            <>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/signup" element={<SaasSignup />} />
            </>
          ) : (
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          )}
          <Route path="/login" element={<Login />} />
          <Route path="/bonus" element={<PublicBonus />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/agendar/:slug" element={<PublicSchedule />} />
          <Route path="/sala/:id" element={<PublicMeetingInvite />} />
          <Route path="/live/:id" element={<PublicMeetingRoom mode="live" />} />
          <Route path="/register/ref/:code" element={<RegisterReferral />} />
          <Route path="/checkout/:type/:target_id" element={<CheckoutPortal />} />
          <Route path="/licencas" element={<ClientLicenseStore />} />
          <Route path="/licencas/:username" element={<ClientLicenseStore />} />
          <Route path="/player-renew/:player_id" element={<PlayerRenewal />} />
          <Route path="/player" element={<WebPlayer />} />

          {/* Shell persistente — Layout/auth não remonta a cada navegação */}
          <Route element={<ProtectedShell />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/attendant/metrics" element={<AttendantMetrics />} />
            <Route path="/chats" element={<SoWhats0311><Chats /></SoWhats0311>} />
            <Route path="/atendimento" element={<SoWhats0311><AtendimentoEspelho /></SoWhats0311>} />
            <Route path="/whatsapp" element={<WhatsAppUnica0311 />} /> {/* ABA_WHATS_UNICA_0311 */}
            <Route path="/financeiro" element={<FinanceiroDashboard />} />
            <Route path="/reunioes" element={<Meetings />} />
            <Route path="/extras" element={<Extras2910 />} />
            <Route path="/reunioes/sala/:id" element={<MeetingRoom mode="booking" />} />
            <Route path="/reunioes/live/:id" element={<MeetingRoom mode="live" />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/funnel" element={<SoWhats0311><Filter /></SoWhats0311>} />
            <Route path="/triggers" element={<AbasAntigasGuard><Triggers /></AbasAntigasGuard>} /> {/* ABAS_ANTIGAS_WHATS_0311 */}
            <Route path="/triggers/chatbot" element={<AbasAntigasGuard><Chatbot /></AbasAntigasGuard>} />
            <Route path="/aurax-ai" element={<AuraXAI />} />
            <Route path="/bonification" element={<Bonification />} />
            <Route path="/recuperacao" element={<AbasAntigasGuard><Recuperacao /></AbasAntigasGuard>} />
            <Route path="/mass-dispatch" element={<Navigate to="/recuperacao" replace />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/logs-financeiros" element={<LogsFinanceiros />} />
            <Route path="/logs-globais" element={<LogsGlobais />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/connections" element={<WhatsAppTudo0311 />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/commissions" element={<Navigate to="/team?tab=commissions" replace />} />
            <Route path="/team/monitor" element={<Navigate to="/team?tab=performance" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/community-support" element={<CommunitySupport />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/api-integrations" element={<ApiIntegrations />} />
            <Route path="/gestor-xui/servers" element={<XuiServers />} />
            <Route path="/permissions" element={<Permissions />} />
            <Route path="/buy-credits" element={<BuyCredits />} />
            <Route path="/postpaid-wallet" element={<Navigate to="/buy-credits" replace />} />
            <Route path="/comunicados" element={<Communications />} />
            <Route path="/notification-templates" element={<NotificationTemplates />} />
            <Route path="/materiais" element={<ResellerManual />} />
            <Route path="/admin/materiais" element={<AdminResellerManual />} />
            <Route path="/gestor-xui/clients" element={<XuiClients />} />
            <Route path="/gestor-xui/live-clients" element={<LiveConnections />} />
            <Route path="/gestor-xui/tests" element={<XuiTests />} />
            <Route path="/gestor-xui/vencimentos" element={<Vencimentos2910 />} />
            <Route path="/gestor-xui/passo-a-passo" element={<PassoAPasso2910 />} />
            <Route path="/gestor-xui/categories" element={<XuiCategories />} />
            <Route path="/gestor-xui/catalog" element={<IptvCatalog />} />
            <Route path="/gestor-xui/partner-apps" element={<PartnerApps />} />
            <Route path="/gestor-xui/own-player" element={<OwnPlayerPage />} />
            <Route path="/app-activations" element={<Hide2810 flag="sem_ativar_apps_2810"><AppActivations /></Hide2810>} />
            <Route path="/gestor-xui/credential-generation" element={<XuiCredentialGeneration />} />
            <Route path="/gestor-xui/resellers" element={<XuiResellers />} />
            <Route path="/gestor-xui/my-portfolio" element={<Hide2810 flag="sem_carteira_2810"><MyPortfolio /></Hide2810>} />
            <Route path="/gestor-xui/reseller-migration" element={<ResellerTreeMigration />} />
            <Route path="/gestor-xui/inactive-resellers" element={<InactiveResellers />} />
            <Route path="/gestor-xui/inactive-clients" element={<InactiveClients />} />
            <Route path="/gestor-xui/packages" element={<Navigate to="/gestor-xui/categories" replace />} />
            <Route path="/gestor-xui/plan-pricing" element={<XuiPlanPricing />} />
            <Route path="/gestor-xui/credit-packs" element={<XuiCreditPacksConfig />} />
            <Route path="/gestor-xui/reseller-billing" element={<ResellerBillingConfig />} />
            <Route path="/gestor-xui/my-reseller-billing" element={<MyResellerBillingConfig />} />
            <Route path="/gestor-xui/gateway" element={<XuiGatewayConfig />} />
            <Route path="/gestor-xui/payments" element={<Navigate to="/gestor-xui/categories" replace />} />
            <Route path="/gestor-xui/credit-logs" element={<XuiCreditLogs />} />
            {saas ? (
              <>
                <Route path="/master/tenants" element={<MasterTenants />} />
                <Route path="/master/setup" element={<MasterSetup />} />
              </>
            ) : null}
          </Route>

          <Route path="/xui-metrics" element={<Navigate to="/dashboard" replace />} />
          <Route path="/gestor-xui/server-recovery" element={<Navigate to="/gestor-xui/servers" replace />} />
          <Route path="/reseller-dns" element={<Navigate to="/permissions" replace />} />
          <Route path="/reseller-manual" element={<Navigate to="/materiais" replace />} />
          <Route path="/admin/reseller-manual" element={<Navigate to="/admin/materiais" replace />} />
          <Route path="/gestor-xui/sales" element={<Navigate to="/sales?tab=vendas" replace />} />
          <Route path="/gestor-xui/checkout-notifications" element={<Navigate to="/sales?tab=avisos" replace />} />

          <Route path="/api-revenda" element={<ChavesApiRevenda0311 />} /> {/* API_REVENDA_0311 */}
          <Route path="/configurar-robo" element={<ConfigurarRobo0311 />} /> {/* CONFIG_ROBO_0311 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
  );
}

function App() {
  React.useEffect(() => {
    // REVENDAS_LOOP_0311: limpar o flag no mount anulava o limite de 1 reload => loop infinito.
    // Agora so limpa apos 15s estavel. Desligar: localStorage REVENDAS_LOOP_0311_OFF=1
    // eslint-disable-next-line no-unused-vars
    let t0311 = null;
    try {
      if (localStorage.getItem('REVENDAS_LOOP_0311_OFF') === '1') {
        sessionStorage.removeItem('aurax_chunk_hard_reload');
      } else {
        t0311 = setTimeout(() => { try { sessionStorage.removeItem('aurax_chunk_hard_reload'); } catch { /* ignore */ } }, 15000);
      }
    } catch {
      /* ignore */
    }
    try {
      // Captura beforeinstallprompt e regista SW o mais cedo possível.
      // eslint-disable-next-line global-require
      const { installPwaCapture } = require('./lib/pwaInstall');
      // eslint-disable-next-line global-require
      const { ensureNotifyServiceWorker } = require('./lib/chatNotify');
      installPwaCapture();
      void ensureNotifyServiceWorker();
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <ThemeProvider>
      <OfficeBackdrop />
    <LanguageProvider>
    <AuthProvider>
    <ConfirmDialogProvider>
    <OfficeFeedbackProvider>
      <BrowserRouter>
        <RouteErrorBoundary>
          <AppRoutes />
        </RouteErrorBoundary>
      </BrowserRouter>
    </OfficeFeedbackProvider>
    </ConfirmDialogProvider>
    </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
